import cloneDeep from 'lodash/cloneDeep'
import dayjs from 'dayjs'
import maxBy from 'lodash/maxBy'

import { AbstractRequestModel } from './Request'
import { SearchResult } from '@/models/Repository'
import BudgetRequestApi from '@/api/BudgetRequest'
import { InsertionOrdersModel } from './InsertionOrders'
import { IOBudgetModel } from './IOBudget'
import { uniqueId } from 'lodash'

const getErrorMessage = rowErrMessage => {
  const errors = [
    {
      response:
        'There is a campaign budget that will be out of period for insertion order',
      display:
        'キャンペーンの配信期間より短い案件予算の期間を申請することはできません',
    },
    {
      response: 'There is a budget overlapping period for insertion order',
      display: '入力された案件予算の期間が他の案件予算と重複しています',
    },
  ]
  let result = '新規案件予算申請の追加に失敗しました'
  errors.some(err => {
    if (rowErrMessage.indexOf(err.response) != -1) {
      result = err.display
      return true
    }
  })
  return result
}

/**
 * TS化するまでのせめてもの型定義
 * FIXME: 全部の型を書くと大変な量になるので必要なものに絞って書いている TS化したら消す
 *
 * @typedef {{
 *   id: number;
 *   budget?: number;
 *   netBudget?: number;
 *   startTime?: string;
 *   endTime?: string;
 *   latestRequest: {
 *     requestStatus: 'waiting' | 'approved' | 'rejected'
 *     startTime: string;
 *     endTime: string;
 *   }
 * }} BudgetType
 * @typedef {{id: number; budgetList: BudgetType[]}} InsretionOrder
 */

export class BudgetRequest extends AbstractRequestModel {
  constructor(params) {
    super(params)
    const {
      agencyId,
      agencyName,
      advertiserId,
      advertiserName,
      advertiserStatus,
      insertionOrderId,
      insertionOrderName,
      insertionOrderBudgetId,
      requestAccountId,
      requestAccountName,
      requestType,
      requestStatus,
      reason,
      budget,
      netBudget,
      startTime,
      endTime,
      requestTime,
    } = params
    this.agencyId = Number(agencyId) || undefined
    this.agencyName = agencyName
    this.advertiserId = Number(advertiserId) || undefined
    this.advertiserName = advertiserName
    this.advertiserStatus = advertiserStatus
    this.insertionOrderId = Number(insertionOrderId) || undefined
    this.insertionOrderName = insertionOrderName
    this.insertionOrderBudgetId = Number(insertionOrderBudgetId) || undefined
    this.requestAccountId = Number(requestAccountId) || undefined
    this.requestAccountName = requestAccountName || undefined
    this.requestType = requestType || undefined
    this.requestStatus = requestStatus || undefined
    this.reason = reason || undefined
    this.budget = budget
    this.netBudget = netBudget
    this.startTime = startTime
    this.endTime = endTime
    this.requestTime = requestTime
    this.IOBudget = null
  }

  clone() {
    return cloneDeep(this)
  }

  /**
   * 承認可能かどうか
   * - 新規申請の場合、 startTime 当日まで承認可能
   */
  get isApprovable() {
    const limitDate = dayjs(this.startTime)
      .startOf('day')
      .add(1, 'day')
    const today = dayjs().startOf('day')
    return (
      super.isApprovable &&
      (this.requestType == 'changed' || today.isBefore(limitDate))
    )
  }

  get isDeletable() {
    const limitDate = dayjs(this.startTime)
      .startOf('day')
      .add(1, 'day')
    const today = dayjs().startOf('day')
    return (
      super.isApprovable &&
      this.requestType == 'delete' &&
      today.isBefore(limitDate)
    )
  }

  async getIOBudget() {
    if (!this.insertionOrderBudgetId) return null
    const io = new InsertionOrdersModel({ id: this.insertionOrderId })
    return await io.getBudget(this.insertionOrderBudgetId)
  }

  async fetchIOBudget() {
    if (!this.insertionOrderBudgetId) return
    this.IOBudget = await this.getIOBudget()
  }

  async approve() {
    const requestStatus = 'approved'
    return await BudgetRequestApi.update(this.id, { requestStatus })
  }

  /**
   * @param {string} reason 却下理由
   */
  async reject(reason) {
    const requestStatus = 'rejected'
    return await BudgetRequestApi.update(this.id, { requestStatus, reason })
  }
}

export class BudgetRequestRepository {
  static async search({
    systemOwnerId,
    requestStatus,
    keyword,
    pageSize,
    pageNo,
    sortBy,
    descending,
  } = {}) {
    const query = {
      systemOwnerId: systemOwnerId,
      requestStatus: requestStatus,
      keyword: keyword,
    }
    const result = await BudgetRequestApi.search(query, pageSize, pageNo)
    const items = result.items
      .map(item => new BudgetRequest(item))
      .filter(item => {
        if (requestStatus === 'all') return true
        return item.requestStatus === requestStatus
      })
      .filter(item => {
        if (
          typeof keyword === 'undefined' ||
          keyword === null ||
          keyword.length === 0
        )
          return true
        return (
          item.insertionOrderName.includes(keyword) ||
          item.advertiserName.includes(keyword)
        )
      })
      .sort((a, b) => {
        if (a[sortBy] > b[sortBy]) {
          return descending ? 1 : -1
        } else if (a[sortBy] < b[sortBy]) {
          return descending ? -1 : 1
        }
        return 0
      })
    return new SearchResult({
      count: result.count,
      pageNo: result.pageNo,
      pageSize,
      items,
    })
  }

  static async bulkApprove(ids) {
    // TODO: API コール
    return Promise.resolve(ids.length > 0)
  }
}

export class BudgetRequestFormFactory {
  static factory(insertionOrder, insertionOrderBudget) {
    if (insertionOrderBudget.isResubmittable) {
      return new ResubmitBudgetRequestForm(insertionOrderBudget)
    } else if (
      insertionOrderBudget.isNew ||
      insertionOrderBudget.isRequestable
    ) {
      return new CreateBudgetRequestForm(insertionOrder, [insertionOrderBudget])
    } else {
      throw new Error(
        `Cannot request for this insertion order budget: ${insertionOrderBudget.id}`,
      )
    }
  }
}

/**
 * 既存の予算を受け取り、
 * 承認待ち、承認済みの予算をフィルタして
 * Period[]: [{start: string, end: string}, ...}] の形に整形して返す。
 *
 * 「ある日付がすでに埋まっている（occupied）こと」を判定するために必要なPeriod[]を取得する。
 */
export const getOccupiedPeriodsForEditBudget = (
  budgetList,
  excludeBudgetId,
) => {
  return [
    ...budgetList
      .filter(
        budget =>
          budget.id !== excludeBudgetId &&
          budget.latestRequest.requestStatus === 'approved',
      )
      .map(budget => ({
        start: budget.startTime,
        end: budget.endTime,
      })),
    // 申請中の予算（新規申請・変更申請共通）
    ...budgetList
      .filter(budget => budget.latestRequest.requestStatus === 'waiting')
      .map(budget => ({
        start: budget.latestRequest.startTime,
        end: budget.latestRequest.endTime,
      })),
  ]
}

export class CreateBudgetRequestForm {
  /**
   * @param {InsretionOrder} insertionOrder
   * @param {{insertionOrder: string; budget: number; startTime: string; endTime: string; isNew: boolean;}[] | undefined} insertionOrderBudgetList
   */
  constructor(insertionOrder, insertionOrderBudgetList) {
    this.insertionOrder = insertionOrder
    this.insertionOrderBudgetList =
      insertionOrderBudgetList && insertionOrderBudgetList.length > 0
        ? insertionOrderBudgetList.map(
            insertionOrderBudget =>
              new CreateBudgetRequestFormItem(insertionOrderBudget),
          )
        : [
            new CreateBudgetRequestFormItem({
              insertionOrderId: insertionOrder.id,
            }),
          ]
  }

  get validationErrors() {
    const validationErrors = {}

    this.insertionOrderBudgetList.forEach((insertionOrderBudget, index) => {
      validationErrors[index] = insertionOrderBudget.validationErrors
    })

    return validationErrors
  }

  get isValid() {
    return this.insertionOrderBudgetList.every(
      insertionOrderBudget => insertionOrderBudget.isValid,
    )
  }

  /**
   * @returns
   *  { start: string, end: string }[]
   */
  getOccupiedPeriods(excludeIndex, excludeBudgetId) {
    const ret = [
      /**
       * 既存の予算(承認済み, 承認待ちのもの)
       */
      // 編集中の予算以外で、承認されている予算
      ...this.insertionOrder.budgetList
        .filter(
          budget =>
            budget.id !== excludeBudgetId &&
            budget.latestRequest.requestStatus === 'approved',
        )
        .map(budget => ({
          start: budget.startTime,
          end: budget.endTime,
        })),
      // 承認待ちの予算
      ...this.insertionOrder.budgetList
        .filter(budget => budget.latestRequest.requestStatus === 'waiting')
        .map(budget => ({
          start: budget.latestRequest.startTime,
          end: budget.latestRequest.endTime,
        })),
      // 変更申請中の予算
      ...this.insertionOrder.budgetList
        .filter(
          budget =>
            budget.latestRequest.requestStatus === 'waiting' &&
            budget.startTime !== undefined &&
            budget.endTime !== undefined,
        )
        .map(budget => ({
          start: budget.startTime,
          end: budget.endTime,
        })),

      /**
       * 新規作成中の予算
       */
      ...this.insertionOrderBudgetList
        .filter(
          (insertionOrderBudgetListItem, index) =>
            // 自分自身を取り除く
            index !== excludeIndex &&
            // startTime, endTimeともに入力されているもの
            (insertionOrderBudgetListItem.startTime !== undefined ||
              insertionOrderBudgetListItem.endTime !== undefined),
        )
        .map(insertionOrderBudgetListItem => ({
          start: insertionOrderBudgetListItem.startTime,
          end: insertionOrderBudgetListItem.endTime,
        })),
    ]
    return ret
  }

  addBudget() {
    const nextToLastBudget = dayjs(
      maxBy(this.getOccupiedPeriods(), d => dayjs(d.end).unix()).end,
    )
      .add(1, 'day')
      .format('YYYY-MM-DD 00:00:00')
    this.insertionOrderBudgetList.push(
      new CreateBudgetRequestFormItem({
        insertionOrderId: this.insertionOrder.id,
        startTime: nextToLastBudget,
        endTime: undefined,
        budget: 0,
      }),
    )
  }

  removeBudget(removeIndex) {
    if (this.insertionOrderBudgetList.length === 1) return

    this.insertionOrderBudgetList = this.insertionOrderBudgetList.filter(
      (_, i) => i !== removeIndex,
    )

    delete this.validationErrors[removeIndex]
  }

  async submit() {
    try {
      // budgetはAPIリクエストの直前までstringで扱いたいので、ここでnumberに変換
      const budgetAddRequest = {
        budgetRequests: this.insertionOrderBudgetList.map(
          insertionOrderBudgetListItem => ({
            ...insertionOrderBudgetListItem,
            budget: parseInt(insertionOrderBudgetListItem.budget),
          }),
        ),
      }

      if (this.insertionOrderBudgetList[0].isNew) {
        const res = await BudgetRequestApi.createBulk(budgetAddRequest)
        return new BudgetRequest(res)
      } else {
        return this.insertionOrderBudgetList[0].submit()
      }
    } catch (e) {
      console.error(e)
      throw new Error(getErrorMessage(e.response.data.message))
    }
  }

  async requestDeleteBudget() {
    try {
      const res = await BudgetRequestApi.requestDeleteBudget(this)
      return new BudgetRequest(res.item)
    } catch (e) {
      console.error(e)
      throw new Error('案件予算の削除申請に失敗しました。')
    }
  }

  toBudgetList() {
    return this.insertionOrderBudgetList.map(
      insertionOrderBudget =>
        new IOBudgetModel({
          ...insertionOrderBudget,
          latestRequest: { ...insertionOrderBudget },
          requests: [{ ...insertionOrderBudget }],
          tempId: insertionOrderBudget.tempId || uniqueId('budget_request_'),
        }),
    )
  }
}

export const getValidationErrors = ({ startTime, endTime, budget }) => {
  const validationErrors = []

  if (startTime === undefined) {
    validationErrors.push('開始日を入力してください')
  }
  if (endTime === undefined) {
    validationErrors.push('終了日を入力してください')
  }
  if (!budget || budget === '0') {
    validationErrors.push('予算を入力してください')
  }
  if (typeof budget === 'string' && isNaN(budget)) {
    validationErrors.push('予算は半角数字で入力してください')
  }
  if (parseInt(budget, 10) <= 0) {
    validationErrors.push('予算は1円以上で入力してください')
  }
  return validationErrors
}

export class CreateBudgetRequestFormItem {
  /**
   * @param {{insertionOrderId?: string; budget?: number; startTime?: string; endTime?: string;} | undefined} insertionOrderBudget
   */
  constructor(insertionOrderBudget) {
    const {
      insertionOrderId,
      budget,
      startTime,
      endTime,
      isNew,
    } = insertionOrderBudget
    this.insertionOrderId = insertionOrderId
    this.insertionOrderBudgetId = insertionOrderBudget.id
    this.budget = budget
    this.isNew = isNew

    const today = dayjs().startOf('day')
    this.startTime = startTime
    // 変更申請かつ、過去の日付の場合、本日を選択した状態にする
    this.endTime =
      !insertionOrderBudget.isNew && dayjs(endTime).isBefore(today)
        ? today.format('YYYY-MM-DD HH:mm:ss')
        : endTime
  }

  get validationErrors() {
    return getValidationErrors({
      startTime: this.startTime,
      endTime: this.endTime,
      budget: this.budget,
    })
  }

  get isValid() {
    return this.validationErrors.length === 0
  }

  async submit() {
    try {
      const res = await BudgetRequestApi.create({
        ...this,
        budget: parseInt(this.budget),
      })
      return new BudgetRequest(res.item)
    } catch (e) {
      console.error(e)
      throw new Error(getErrorMessage(e.response.data.message))
    }
  }

  async requestDeleteBudget() {
    try {
      const res = await BudgetRequestApi.requestDeleteBudget(this)
      return new BudgetRequest(res.item)
    } catch (e) {
      console.error(e)
      throw new Error('案件予算の削除申請に失敗しました。')
    }
  }

  toBudget() {
    return new IOBudgetModel({
      ...this,
      latestRequest: { ...this },
      requests: [{ ...this }],
    })
  }
}

export class ResubmitBudgetRequestForm {
  constructor(insertionOrderBudget) {
    const { budget, startTime, endTime } = insertionOrderBudget.latestRequest
    this.insertionOrderBudgetRequestId = insertionOrderBudget.latestRequest.id
    this.budget = budget

    const today = dayjs().startOf('day')
    this.startTime = dayjs(startTime).isBefore(today)
      ? today.format('YYYY-MM-DD HH:mm:ss')
      : startTime
    this.endTime = dayjs(endTime).isBefore(today) ? null : endTime
  }

  get isValid() {
    return this.startTime && this.endTime && this.budget > 0
  }

  async submit() {
    try {
      const res = await BudgetRequestApi.resubmit(this)
      return new BudgetRequest(res.item)
    } catch (e) {
      console.error(e)
      throw new Error('案件予算再申請に失敗しました')
    }
  }

  toBudget() {
    return new IOBudgetModel({
      ...this,
      latestRequest: { ...this },
      requests: [{ ...this }],
    })
  }
}
